/**
 * Purpose: Business Detail page shell — composition only. Data/permissions come
 *          from useBusinessDetail; sections come from the module registry via
 *          ModuleRenderer; this file knows no individual modules.
 * Inputs: Route params (business slug/id, optional societySlug).
 * Outputs: Hero + action bars + module column + sticky sidebar + related rail +
 *          mobile bottom CTA; skeletons while loading.
 */
import React, { useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import PageLayout from '../components/layout/PageLayout';
import useBusinessDetail from '../hooks/useBusinessDetail';
import useImageLightbox from '../hooks/useImageLightbox';
import BusinessHero from '../components/businesses/detail/BusinessHero';
import VisitorActionBar from '../components/businesses/detail/VisitorActionBar';
import OwnerActionBar from '../components/businesses/detail/OwnerActionBar';
import ModuleRenderer from '../components/businesses/detail/ModuleRenderer';
import StickySidebar from '../components/businesses/detail/StickySidebar';
import MobileBottomCTA from '../components/businesses/detail/MobileBottomCTA';
import RelatedBusinesses from '../components/businesses/detail/RelatedBusinesses';
import { HeroSkeleton, SidebarSkeleton, ModuleSkeleton } from '../components/businesses/detail/BusinessDetailSkeletons';
import { CTAButton } from '../components/ui';
import { getSlugFromParams } from '../utils/urlHelpers';
import { getModuleLabel } from '../config/businessModules';
import { normalizeGalleryImages } from '../utils/imageLightbox';

const useFormState = () => {
  const [state, setState] = useState({ show: false, editing: null });
  return {
    ...state,
    openNew: () => setState({ show: true, editing: null }),
    openEdit: (item) => setState({ show: true, editing: item }),
    close: () => setState({ show: false, editing: null }),
  };
};

const StatusScreen = ({ icon, title, message, onBack, onMobileBack }) => (
  <PageLayout title="PakPoints" onMobileBack={onMobileBack}>
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="max-w-md text-center">
        <div className="mb-4 text-5xl" aria-hidden>{icon}</div>
        <h2 className="mb-2 text-2xl font-bold text-gray-900 dark:text-gray-100">{title}</h2>
        <p className="mb-6 text-gray-600 dark:text-gray-300">{message}</p>
        <CTAButton onClick={onBack}>Back to Businesses</CTAButton>
      </div>
    </div>
  </PageLayout>
);

const BusinessDetailPage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const societySlug = params.societySlug || null;
  const listingsFallbackPath = societySlug ? `/societies/${societySlug}/businesses` : '/businesses';

  const businessIdentifier = getSlugFromParams(params, 'business');
  const {
    business,
    loading,
    error,
    views,
    canManage,
    modules,
    moduleCtx,
    relatedBusinesses,
    reviewsLoading,
    refreshCatalogue,
    deleteProduct,
    deleteService,
    toggleProductAvailability,
    toggleServiceAvailability,
    submitReview,
  } = useBusinessDetail(businessIdentifier);

  const { openLightbox, Lightbox } = useImageLightbox();
  const productForm = useFormState();
  const serviceForm = useFormState();

  const handleMobileBack = useCallback(() => {
    const idx = typeof window !== 'undefined' ? window.history.state?.idx : undefined;
    if (typeof idx === 'number' && idx > 0) {
      navigate(-1);
      return;
    }
    if (location.key !== 'default') {
      navigate(-1);
      return;
    }
    navigate(listingsFallbackPath);
  }, [location.key, navigate, listingsFallbackPath]);

  const handleProductChanged = () => {
    productForm.close();
    refreshCatalogue();
  };

  const handleServiceChanged = () => {
    serviceForm.close();
    refreshCatalogue();
  };

  if (loading) {
    return (
      <PageLayout title="PakPoints" onMobileBack={handleMobileBack}>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
          <HeroSkeleton />
          <div className="mx-auto grid max-w-screen-2xl gap-6 px-4 pb-10 sm:px-6 lg:grid-cols-3 lg:px-8">
            <div className="space-y-6 lg:col-span-2">
              <ModuleSkeleton />
              <ModuleSkeleton />
            </div>
            <SidebarSkeleton />
          </div>
        </div>
      </PageLayout>
    );
  }

  if (error || !business) {
    return (
      <StatusScreen
        icon={error ? '⚠️' : '🏢'}
        title="Business Not Found"
        message={error || "The business you're looking for doesn't exist."}
        onBack={() => navigate(listingsFallbackPath)}
        onMobileBack={handleMobileBack}
      />
    );
  }

  const catalogueLabel = getModuleLabel(business, 'catalogue', 'Products');
  const moduleProps = {
    openLightbox,
    productForm,
    serviceForm,
    onProductChanged: handleProductChanged,
    onServiceChanged: handleServiceChanged,
    onDeleteProduct: deleteProduct,
    onDeleteService: deleteService,
    onToggleProductAvailability: toggleProductAvailability,
    onToggleServiceAvailability: toggleServiceAvailability,
    onSubmitReview: submitReview,
    reviewsLoading,
  };

  return (
    <PageLayout title={`${business.name} - PakPoints`} onMobileBack={handleMobileBack}>
      <div className="min-h-screen bg-gray-50 pb-24 dark:bg-gray-950 lg:pb-10">
        <BusinessHero
          business={business}
          views={views}
          onCoverClick={
            business.images?.length
              ? () => openLightbox(normalizeGalleryImages(business.images, business.name), 0)
              : undefined
          }
        />

        <div className="mx-auto max-w-screen-2xl space-y-3 px-4 sm:px-6 lg:px-8">
          <VisitorActionBar business={business} />
          {canManage && (
            <OwnerActionBar
              business={business}
              addProductLabel={catalogueLabel === 'Menu' ? 'Add menu item' : 'Add product'}
              onAddProduct={productForm.openNew}
              onAddService={serviceForm.openNew}
            />
          )}

          <div className="grid gap-6 pt-2 lg:grid-cols-3">
            <main className="min-w-0 lg:col-span-2">
              <ModuleRenderer modules={modules} moduleCtx={moduleCtx} moduleProps={moduleProps} />
            </main>
            <aside className="min-w-0 lg:sticky lg:top-20 lg:self-start" aria-label="Business information">
              <StickySidebar business={business} views={views} />
            </aside>
          </div>

          <RelatedBusinesses businesses={relatedBusinesses} societySlug={societySlug} />
        </div>
      </div>

      <MobileBottomCTA business={business} canManage={canManage} />
      {Lightbox}
    </PageLayout>
  );
};

export default BusinessDetailPage;
